// ─────────────────────────────────────────────────────────────
// TermsModal.js
// Terms of Service. The organization and its town come from SITE (see
// site.config.js) so a sister site's terms name that group. Written primarily to satisfy the
// toll-free SMS verification requirement for a public T&C covering the
// messaging program: what messages are sent, how often, that joining is free,
// and how to stop. Mirrors PrivacyPolicyModal.js in structure — the body is
// exported separately so the /terms route and the footer modal share one copy.
//
// Section 2 must stay consistent with three other places or the messaging
// program stops matching its own disclosures:
//   - the opt-in fine print in EventCalendar.js
//   - the HELP auto-reply in functions/index.js (receiveSms)
//   - the SMS section of PrivacyPolicyModal.js
// ─────────────────────────────────────────────────────────────

import React from 'react';
import SITE from '../site';

const LAST_UPDATED = 'September 2026';

export function TermsBody() {
  return (
    <>
        <div className="modal-header">
          <h2>Terms of Service</h2>
          <p className="modal-org">{SITE.name} · Last updated {LAST_UPDATED}</p>
        </div>

        <div className="modal-waiver-text">

          <p>
            {SITE.name} ("we," "us," or "our") operates this website to organize
            community pickup ultimate frisbee games in {SITE.locality}, {SITE.regionName}. By using
            this site, RSVPing for a game, or joining our text messaging program, you
            agree to these terms.
          </p>

          <p><strong>1. About This Service</strong></p>
          <p>
            This is a free, volunteer-run service for a local pickup sports group. There
            is no fee to use the site, RSVP for games, or receive our text messages. No
            account is required. We do not sell anything.
          </p>

          <p><strong>2. Text Messaging Program</strong></p>
          <p>
            <em>What you get:</em> If you provide your phone number and check the opt-in
            box, {SITE.name} will text you game reminders, schedule changes,
            cancellations, and confirmations of your RSVP.
          </p>
          <p>
            <em>How often:</em> Message frequency varies with the game schedule, typically
            1–3 messages per week during the season.
          </p>
          <p>
            <em>Cost:</em> We charge nothing for these messages. Message and data rates
            may apply from your mobile carrier.
          </p>
          <p>
            <em>How to stop:</em> Reply STOP to any message at any time. You will receive
            one confirmation and no further messages. You may also reply UNSUBSCRIBE,
            CANCEL, END, QUIT, or STOPALL. To rejoin, reply START or opt in again on the
            website.
          </p>
          <p>
            <em>How to get help:</em> Reply HELP to any message, or contact the organizers
            through this website.
          </p>
          <p>
            <em>Carriers:</em> Mobile carriers are not liable for delayed or undelivered
            messages. Delivery is not guaranteed, and you should not rely solely on text
            messages for time-sensitive information — the website is always the
            authoritative source for game status.
          </p>
          <p>
            <em>Consent:</em> You may only opt in a phone number you personally control.
            Providing someone else's number is not permitted. Consent to receive texts is
            never a condition of RSVPing or participating in a game.
          </p>

          <p><strong>3. RSVPs and Participation</strong></p>
          <p>
            RSVPs help us decide whether a game has enough players to run. They are a
            courtesy, not a commitment, and you may change or remove yours at any time.
            We may remove RSVPs that appear to be duplicates, spam, or submitted on behalf
            of someone else.
          </p>

          <p><strong>4. Assumption of Risk</strong></p>
          <p>
            Ultimate frisbee is a physical sport carrying an inherent risk of injury.
            Games are community-organized and are not supervised, coached, refereed, or
            insured by any organization. You participate entirely at your own risk and
            are responsible for your own health, safety, and equipment. Participants under
            18 require parental consent — see the Parental Consent form linked in the site
            footer.
          </p>

          <p><strong>5. Acceptable Use</strong></p>
          <p>
            Please use this site as intended: to RSVP honestly for games you plan to
            attend. Do not submit false RSVPs, impersonate others, post abusive content in
            comment areas, or attempt to disrupt the service. We may remove content and
            block access at our discretion.
          </p>

          <p><strong>6. No Warranty</strong></p>
          <p>
            This site is provided "as is," without warranties of any kind. We do not
            guarantee it will be available, accurate, or error-free. Games may be
            cancelled, rescheduled, or relocated — sometimes on short notice and sometimes
            without a notification reaching you.
          </p>

          <p><strong>7. Limitation of Liability</strong></p>
          <p>
            To the fullest extent permitted by law, {SITE.name} and its organizers
            are not liable for any injury, loss, or damage arising from participation in
            games, use of this site, or reliance on any message sent through it.
          </p>

          <p><strong>8. Privacy</strong></p>
          <p>
            Our handling of your information is described in our Privacy Policy, available
            at <a href="/privacy">/privacy</a> and from the site footer.
          </p>

          <p><strong>9. Changes to These Terms</strong></p>
          <p>
            We may update these terms from time to time. The "last updated" date above
            will reflect any changes. Continued use of the site after a change constitutes
            acceptance of the updated terms.
          </p>

          <p><strong>10. Contact</strong></p>
          <p>
            Questions about these terms? Contact the {SITE.name} organizers through
            this website or by replying to any text message.
          </p>

        </div>
    </>
  );
}

function TermsModal({ onClose }) {
  return (
    <div className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>×</button>
        <TermsBody />
        <button className="submit-btn" style={{marginTop:'1rem', width:'100%'}}
          onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export default TermsModal;
